import React from "react";
import { Box, Typography, styled, keyframes } from "@mui/material";

// Import project components
import LandfillMethane from "../waste/LandfillMethane";
import MehmoodBooti from "../waste/MehmoodBooti";
import LegacyDumpsite from "../waste/LegacyDumpsite";
import CityRecycling from "../waste/CityRecycling";
import Composting from "../waste/Composting";
import WasteToEnergy from "../waste/WasteToEnergy";
import BSFProject from "../waste/BSFProject";
import OffalToBiogas from "../waste/OffalToBiogas";

const butterflyFly = keyframes`
  to {
    transform: translate(
        calc(-50% + var(--translate-x)),
        calc(-50% + var(--translate-y))
      )
      scale(1);
  }
`;

const ProfessionalModalHeader = styled(Box)({
  textAlign: "center",
  marginBottom: "32px",
  animation: "fadeInDown 0.8s ease-out",
  "@keyframes fadeInDown": {
    "0%": { opacity: 0, transform: "translateY(-30px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
});

const HeroSection = styled(Box)({
  position: "relative",
  width: "100%",
  height: "650px",
  background: "#000000ff",
  marginBottom: "0px",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const LogoCircle = styled(Box)({
  display: "flex",
  // background: "rgba(255, 255, 255, 0.95)",
  borderRadius: "50%",
  width: "200px",
  height: "200px",
  left: "37%",
  // boxShadow: "0 8px 20px rgba(45, 134, 89, 0.2)",
  position: "relative",
  bottom: "10%",
  zIndex: 10,
  animation: "floatUp 1s ease",
  "@keyframes floatUp": {
    from: {
      opacity: 0,
      transform: "translateY(20px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
});

const ButterflyContainer = styled(Box)({
  position: "relative",
  top: "50%",
  left: "32%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  backgroundColor: "#000000ff",
  zIndex: 5,
});

const ButterflyButton = styled(Box)(({ index, totalItems }) => {
  const angle = (index * 360) / totalItems;
  const radius = 250;
  const translateX = radius * Math.cos((angle * Math.PI) / 180);
  const translateY = radius * Math.sin((angle * Math.PI) / 180);

  return {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%) scale(0)",
    maxWidth: "280px",
    minHeight: "60px",
    padding: "12px 16px",
    background:
      "linear-gradient(135deg, #2d8659 0%, #000000ff 50%, #4caf50 100%)",
    color: "white",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    borderRadius: "25px",
    cursor: "pointer",
    fontSize: "0.75rem",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    textAlign: "center",
    boxShadow: "0 4px 15px rgba(45, 134, 89, 0.2)",
    willChange: "transform",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    lineHeight: 1.3,
    pointerEvents: "auto",
    animation: `${butterflyFly} 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) ${
      index * 0.1 + 0.5
    }s both`,
    "--translate-x": `${translateX}px`,
    "--translate-y": `${translateY}px`,
    "&:hover": {
      background: "linear-gradient(135deg, #1e5631 0%, #2d8659 100%)",
      boxShadow: "0 6px 20px rgba(45, 134, 89, 0.4)",
      transform: `translate(calc(-50% + ${translateX}px), calc(-50% + ${translateY}px)) scale(1.05)`,
    },
  };
});

const ButterflyIcon = styled(Box)({
  fontSize: "1.5rem",
  flexShrink: 0,
  animation: "spin 3s linear infinite",
  "@keyframes spin": {
    from: {
      transform: "rotate(0deg)",
    },
    to: {
      transform: "rotate(360deg)",
    },
  },
});

const ButterflyText = styled(Typography)({
  fontSize: "1.3rem",
  fontWeight: 700,
  wordWrap: "break-word",
  overflowWrap: "break-word",
  hyphens: "auto",
});

const WasteToValueProjects = ({ onProjectClick }) => {
  const menuItems = [
    {
      id: 1,
      title: "Landfill Methane Valorization",
      component: LandfillMethane,
    },
    {
      id: 2,
      title: "Dumpsite Rehabilitation",
      component: MehmoodBooti,
    },
    {
      id: 3,
      title: "Legacy Dumpsite Restoration",
      component: LegacyDumpsite,
    },
    {
      id: 4,
      title: "City Recycling Green Park",
      component: CityRecycling,
    },
    {
      id: 5,
      title: "500 TPD City Composting Facility",
      component: Composting,
    },
    {
      id: 6,
      title: "25 MW Waste-to-Energy Project",
      component: WasteToEnergy,
    },
    {
      id: 7,
      title: "Black Soldier Fly (BSF) Pilot Project",
      component: BSFProject,
    },
    {
      id: 8,
      title: "Offal To Biogas",
      component: OffalToBiogas,
    },
  ];

  const handleButtonClick = (item) => {
    if (onProjectClick) {
      onProjectClick(item);
    }
  };

  return (
    <>
      <ProfessionalModalHeader>
        <Typography
          sx={{
            fontSize: "3rem",
            fontWeight: 800,
            background:
              "linear-gradient(135deg, #65e06dff 0%, #000000 50%, #65e06dff 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: "8px",
            "@media (max-width: 768px)": {
              fontSize: "2.2rem",
            },
          }}
        >
          ♻️ Waste-to-Value Projects ♻️
        </Typography>
        <Typography
          sx={{
            fontSize: "1.8rem",
            color: "#404040ff",
            fontWeight: 700,
          }}
        >
          7 Innovative Projects Transforming Waste into Resources
        </Typography>
      </ProfessionalModalHeader>

      <HeroSection>
        <LogoCircle>
          <Box
            component="img"
            src="/images/WasteToValue.png"
            alt="Waste to Value Logo"
            sx={{
              width: 350,
              height: 350,

              borderRadius: "50%",
              objectFit: "contain",
              boxShadow: 3,
              // backgroundColor: "white",
              p: 1,
            }}
          />
        </LogoCircle>

        <ButterflyContainer className="butterfly-container">
          {menuItems.map((item, index) => (
            <ButterflyButton
              key={item.id}
              index={index}
              totalItems={menuItems.length}
              className="butterfly-button"
              onClick={() => handleButtonClick(item)}
              title={item.title}
            >
              <ButterflyIcon className="butterfly-icon">✨</ButterflyIcon>
              <ButterflyText className="butterfly-text">
                {item.title}
              </ButterflyText>
            </ButterflyButton>
          ))}
        </ButterflyContainer>
      </HeroSection>
    </>
  );
};

export default WasteToValueProjects;
